import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Bell,
  BookOpen,
  CalendarClock,
  Clock,
  Download,
  Smartphone,
  Globe,
  LifeBuoy,
  ListChecks,
  MessageSquarePlus,
  Palette,
  Plug,
  Settings,
  Sliders,
  Trash2,
  User,
} from 'lucide-react';
import {
  CONTEXTS_MAX,
  settingSave,
  SettingSaveUnconfirmed,
  GUIDE_TITLE,
  WEEKDAY_NAMES,
  WEEKLY_REVIEW_DEFAULT_TIME,
  contextRenames,
  screenOfPath,
  validateGtdContexts,
} from '@cerebro-diem/core';
import { useFeedbackDialog } from '../../stores/feedbackStore';
import type { Profile } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { saveGtdContexts, updateProfile, useProfile, useTimeZones } from '../../services/profile';
import type { ProfileUpdate } from '../../services/profile';
import { useAuthStore } from '../../stores/authStore';
import { inputClass, outlineButton, primaryButton, tapTarget } from '../../components/styles';
import { describeTimeZone } from '@cerebro-diem/core';
import { useTimeZoneSetup } from '../../stores/timeZoneStore';
import NotificationsSetting from './NotificationsSetting';
import AboutApp from './AboutApp';
import ExportData from './ExportData';
import ThemeSetting from './ThemeSetting';
import RecentCaptures from './RecentCaptures';
import SystemStatus from './SystemStatus';
import ConnectorsSetting from './ConnectorsSetting';

const DISPLAY_NAME_MAX = 80;
const THRESHOLD_MIN = 0.5;
const THRESHOLD_MAX = 0.95;
const THRESHOLD_STEP = 0.05;

/**
 * Saving one setting, with an honest state: what is being saved, that it was
 * saved, or why it was not confirmed. Reconciliation never holds the save
 * pending or replaces the entered draft; confirmed writes update the cache.
 */
function useSetting(userId: string | undefined, setNotice: (notice: string | null) => void) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ update, ownerId }: { update: ProfileUpdate; ownerId?: string }) =>
      settingSave(signal => updateProfile(ownerId, update, signal)),
    onMutate: () => setNotice(null),
    onSuccess: (saved, { update, ownerId }) => {
      if (useAuthStore.getState().user?.id !== ownerId) return;
      queryClient.setQueryData<Profile>(queryKeys.profile, current => {
        if (!current || current.id !== ownerId) return current;
        const next = { ...current };
        const copyConfirmed = <K extends keyof ProfileUpdate>(key: K) => { next[key] = saved[key]; };
        (Object.keys(update) as Array<keyof ProfileUpdate>).forEach(copyConfirmed);
        return next;
      });
      setNotice('Saved.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
    onError: (_error, { ownerId }) => {
      if (useAuthStore.getState().user?.id !== ownerId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
  return { ...mutation, mutate: (update: ProfileUpdate) => mutation.mutate({ update, ownerId: userId }) };
}

function SaveState({
  mutation,
  invalid,
  notice,
}: {
  mutation: { isPending: boolean; isError: boolean; error: unknown };
  invalid?: string | null;
  /** The current account's last confirmed save. */
  notice: string | null;
}) {
  if (invalid) return <p className="text-sm text-error">{invalid}</p>;
  if (mutation.isPending) return <p className="text-sm text-on-surface-variant">Saving…</p>;
  if (mutation.isError) return <p className="text-sm text-error">{mutation.error instanceof SettingSaveUnconfirmed ? 'Save unconfirmed' : 'Not saved'}: {errorMessage(mutation.error)}</p>;
  if (notice) return <p className="text-sm text-success">{notice}</p>;
  return null;
}

type SettingName = 'name' | 'zone' | 'digest' | 'weeklyReview' | 'threshold' | 'contexts';

interface SettingProps {
  profile: Profile;
  userId: string | undefined;
  notice: string | null;
  setNotice: (notice: string | null) => void;
}

function Section({ icon: Icon, title, id, children }: { icon: typeof User; title: string; id?: string; children: ReactNode }) {
  return (
    <section id={id} className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm scroll-mt-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">
        <Icon className="w-5 h-5" />
        {title}
      </h2>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function SettingsPage() {
  const { user, signOut } = useAuthStore();
  // A refused sign-out leaves the user signed in (the store keeps the session), so say so here.
  const signOutMutation = useMutation({ mutationFn: signOut });
  const location = useLocation();
  // Arrived from Today's "no device" warning (/settings#notifications): scrolled to Notifications.
  useEffect(() => {
    if (location.hash === '#notifications') document.getElementById('notifications')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash]);
  const openFeedback = useFeedbackDialog((state) => state.open);
  const profileQuery = useProfile();
  const profile = profileQuery.data;
  // Each setting's last result belongs to the current account.
  const [notices, setNotices] = useState<Partial<Record<SettingName, string | null>>>({});
  useEffect(() => setNotices({}), [user?.id]);
  const noticeOf = (name: SettingName) => ({
    notice: notices[name] ?? null,
    setNotice: (notice: string | null) => setNotices((current) => ({ ...current, [name]: notice })),
  });


  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Settings className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      <Section icon={LifeBuoy} title="Help">
        <div className="flex flex-wrap gap-2">
          <Link to="/how-it-works" className={`${outlineButton} inline-flex items-center gap-2`}>
            <BookOpen className="w-4 h-4" />
            {GUIDE_TITLE}
          </Link>
          <button type="button" onClick={() => openFeedback(screenOfPath(location.pathname))} className={`${outlineButton} inline-flex items-center gap-2`}>
            <MessageSquarePlus className="w-4 h-4" />
            Send feedback
          </button>
          <Link to="/feedback" className={`${outlineButton} inline-flex items-center gap-2`}>
            <ListChecks className="w-4 h-4" />
            Your feedback
          </Link>
        </div>
      </Section>

      <Section icon={Bell} title="Notifications" id="notifications">
        <NotificationsSetting />
      </Section>

      <Section icon={Plug} title="Connectors">
        <ConnectorsSetting />
      </Section>

      <SystemStatus />

      <RecentCaptures />

      <Section icon={Palette} title="Appearance">
        <ThemeSetting />
      </Section>

      {profileQuery.isError && <p className="text-sm text-error">Could not load your settings: {errorMessage(profileQuery.error)}</p>}
      {profileQuery.isPending && <p className="text-sm text-on-surface-variant">Loading…</p>}

      {profile && (
        <>
          <Section icon={User} title="Account">
            <div>
              <p className="text-sm text-on-surface-variant">Email</p>
              <p className="font-medium">{user?.email}</p>
            </div>
            <DisplayNameSetting key={`name:${user?.id}`} profile={profile} userId={user?.id} {...noticeOf('name')} />
          </Section>

          <Section icon={Sliders} title="Preferences">
            <TimeZoneSetting key={`zone:${user?.id}`} profile={profile} userId={user?.id} {...noticeOf('zone')} />
            <DigestTimeSetting key={`digest:${user?.id}`} profile={profile} userId={user?.id} {...noticeOf('digest')} />
            <WeeklyReviewSetting
              key={`weekly:${user?.id}`}
              profile={profile}
              userId={user?.id}
              {...noticeOf('weeklyReview')}
            />
            <ThresholdSetting key={`threshold:${user?.id}`} profile={profile} userId={user?.id} {...noticeOf('threshold')} />
            <div>
              <p className="text-sm text-on-surface-variant">Voice capture mode</p>
              <p className="font-medium">{profile.voice_mode === 'tap_to_start' ? 'Tap to start' : 'Push to talk'}</p>
            </div>
          </Section>

          <Section icon={ListChecks} title="Contexts">
            <ContextsSetting key={`contexts:${user?.id}`} profile={profile} {...noticeOf('contexts')} />
          </Section>
        </>
      )}


      {/* About: the website's version; the Android app only for a browser on Android. */}
      <section aria-labelledby="about-heading" className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm">
        <h2 id="about-heading" className="flex items-center gap-2 text-lg font-semibold mb-4">
          <Smartphone className="w-5 h-5" />
          About
        </h2>
        <AboutApp />
      </section>

      {/* Data */}
      <section className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">
          <Download className="w-5 h-5" />
          Data
        </h2>
        <ExportData />
      </section>

      {/* Danger Zone */}
      <section className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm border border-error/30">
        <h2 className="text-lg font-semibold mb-4 text-error">Danger Zone</h2>
        <div className="flex flex-col items-start gap-4">
          <button
            type="button"
            onClick={() => signOutMutation.mutate()}
            disabled={signOutMutation.isPending}
            className={`inline-flex items-center ${tapTarget} px-4 py-2 border border-error text-error rounded-xl hover:bg-error/10 transition-colors`}
          >
            Sign Out
          </button>
          {signOutMutation.isError && (
            <p role="alert" className="text-sm text-error">
              Not signed out: {errorMessage(signOutMutation.error)}
            </p>
          )}
          <button className={`inline-flex items-center ${tapTarget} px-4 py-2 bg-error text-on-error rounded-xl hover:bg-error/90 transition-colors`}>
            Delete Account
          </button>
        </div>
      </section>
    </div>
  );
}

function DisplayNameSetting({ profile, userId, notice, setNotice }: SettingProps) {
  const [name, setName] = useState(profile.display_name ?? '');
  const mutation = useSetting(userId, setNotice);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({ display_name: name.trim() || null });
  };
  return (
    <form onSubmit={submit} className="space-y-1">
      <label htmlFor="display-name" className="text-sm text-on-surface-variant">
        Display name
      </label>
      <div className="flex gap-2">
        <input
          id="display-name"
          className={`${inputClass} min-w-0 flex-1`}
          value={name}
          maxLength={DISPLAY_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          placeholder="Not set"
        />
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          Save
        </button>
      </div>
      <SaveState mutation={mutation} notice={notice} />
    </form>
  );
}

function TimeZoneSetting({ profile, userId, notice, setNotice }: SettingProps) {
  const zonesQuery = useTimeZones();
  const [zone, setZone] = useState(profile.timezone ?? '');
  const [invalid, setInvalid] = useState<string | null>(null);
  const mutation = useSetting(userId, setNotice);
  const zones = zonesQuery.data ?? [];
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Why this browser's zone did not reach the profile at sign-in, if it did not.
  const zoneProblem = useTimeZoneSetup((state) => state.problem);
  const suggestDevice = zones.includes(deviceZone) && deviceZone !== zone;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!zones.includes(zone)) {
      setInvalid('Choose a time zone from the list.');
      return;
    }
    setInvalid(null);
    mutation.mutate({ timezone: zone });
  };
  return (
    <form onSubmit={submit} className="space-y-1">
      <label htmlFor="time-zone" className="text-sm text-on-surface-variant flex items-center gap-2">
        <Globe className="w-4 h-4" />
        Time zone
      </label>
      <div className="flex flex-wrap gap-2">
        <input
          id="time-zone"
          className={`${inputClass} flex-1 min-w-48`}
          list="time-zone-names"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          placeholder="Search, e.g. America/Chicago"
          autoComplete="off"
        />
        <datalist id="time-zone-names">
          {zones.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <button type="submit" disabled={mutation.isPending || !zonesQuery.isSuccess} className={primaryButton}>
          Save
        </button>
      </div>
      {!profile.timezone && <p className="text-sm text-tertiary">{describeTimeZone(null, deviceZone, zoneProblem)}</p>}
      {suggestDevice && (
        <button type="button" onClick={() => setZone(deviceZone)} className={`${outlineButton} text-xs`}>
          Use this device's time zone ({deviceZone})
        </button>
      )}
      {zonesQuery.isError && <p className="text-sm text-error">Could not load the time zones: {errorMessage(zonesQuery.error)}</p>}
      <SaveState mutation={mutation} invalid={invalid} notice={notice} />
    </form>
  );
}

function DigestTimeSetting({ profile, userId, notice, setNotice }: SettingProps) {
  const [time, setTime] = useState(profile.digest_time ? profile.digest_time.slice(0, 5) : '08:00');
  const mutation = useSetting(userId, setNotice);
  const off = profile.digest_time === null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({ digest_time: `${time}:00` });
  };
  return (
    <form onSubmit={submit} className="space-y-1">
      <label htmlFor="digest-time" className="text-sm text-on-surface-variant flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Daily digest {off ? '(off)' : 'time'}
      </label>
      <div className="flex gap-2">
        <input id="digest-time" type="time" className={`${inputClass} min-w-0 flex-1`} value={time} onChange={(e) => setTime(e.target.value)} required />
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          {off ? 'Turn on' : 'Save'}
        </button>
        {!off && (
          <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate({ digest_time: null })} className={outlineButton}>
            Turn off
          </button>
        )}
      </div>
      <p className="text-xs text-on-surface-variant">
        Your morning check-in already picks today's focus; with it on, you can turn the digest off and keep one morning prompt.
      </p>
      <SaveState mutation={mutation} notice={notice} />
    </form>
  );
}

function WeeklyReviewSetting({ profile, userId, notice, setNotice }: SettingProps) {
  // The day carries the whole choice: '' is off, and the time it would arrive
  // at is kept ready so turning it back on has a time to save.
  const [day, setDay] = useState(profile.weekly_review_day === null ? '' : String(profile.weekly_review_day));
  const [time, setTime] = useState(profile.weekly_review_day === null ? WEEKLY_REVIEW_DEFAULT_TIME : profile.weekly_review_time.slice(0, 5));
  const mutation = useSetting(userId, setNotice);
  const off = day === '';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate(off ? { weekly_review_day: null } : { weekly_review_day: Number(day), weekly_review_time: `${time}:00` });
  };
  return (
    <form onSubmit={submit} className="space-y-1">
      <label htmlFor="weekly-review-day" className="text-sm text-on-surface-variant flex items-center gap-2">
        <CalendarClock className="w-4 h-4" />
        Weekly review
      </label>
      <div className="flex flex-wrap gap-2">
        <select id="weekly-review-day" className={`${inputClass} min-w-0 flex-1`} value={day} onChange={(e) => setDay(e.target.value)}>
          <option value="">Off</option>
          {WEEKDAY_NAMES.map((name, number) => (
            <option key={name} value={number}>
              {name}
            </option>
          ))}
        </select>
        <input
          id="weekly-review-time"
          aria-label="Weekly review time"
          type="time"
          className={`${inputClass} min-w-0 flex-1`}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={off}
          required
        />
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          Save
        </button>
      </div>
      <p className="text-sm text-on-surface-variant">A look back over the week just gone and ahead to the week coming.</p>
      <SaveState mutation={mutation} notice={notice} />
    </form>
  );
}

function ThresholdSetting({ profile, userId, notice, setNotice }: SettingProps) {
  const [threshold, setThreshold] = useState(Number(profile.confidence_threshold));
  const mutation = useSetting(userId, setNotice);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({ confidence_threshold: Math.round(threshold * 100) / 100 });
  };
  return (
    <form onSubmit={submit} className="space-y-1">
      <label htmlFor="threshold" className="text-sm text-on-surface-variant">
        Auto-file when the AI is at least {Math.round(threshold * 100)}% sure; below that, captures wait in the Inbox.
      </label>
      <div className="flex items-center gap-3">
        <input
          id="threshold"
          type="range"
          min={THRESHOLD_MIN}
          max={THRESHOLD_MAX}
          step={THRESHOLD_STEP}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          className={`min-w-0 flex-1 accent-primary ${tapTarget}`}
        />
        <span className="w-12 shrink-0 text-right font-medium">{Math.round(threshold * 100)}%</span>
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          Save
        </button>
      </div>
      <SaveState mutation={mutation} notice={notice} />
    </form>
  );
}

interface ContextItem {
  /** The name it had when editing began; null for an added one. */
  original: string | null;
  value: string;
}

const itemsOf = (contexts: readonly string[]): ContextItem[] => contexts.map((context) => ({ original: context, value: context }));

function ContextsSetting({ profile, notice, setNotice }: Omit<SettingProps, 'userId'>) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<ContextItem[]>(() => itemsOf(profile.gtd_contexts));
  const [added, setAdded] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);
  // One database call saves the list and gives renamed contexts' tasks the new name: all of it or none.
  const mutation = useMutation({
    mutationFn: ({ contexts, renames, ownerId }: { contexts: string[]; renames: ReadonlyMap<string, string>; ownerId?: string }) =>
      settingSave(signal => saveGtdContexts(contexts, renames, signal, ownerId)),
    onMutate: () => setNotice(null),
    onSuccess: (renamed, { contexts, ownerId }) => {
      if (useAuthStore.getState().user?.id !== ownerId) return;
      queryClient.setQueryData<Profile>(queryKeys.profile, profile => profile ? { ...profile, gtd_contexts: contexts } : profile);
      setNotice(renamed > 0 ? `Saved. ${renamed} ${renamed === 1 ? 'task moved' : 'tasks moved'} to the new names.` : 'Saved.');
      setItems(itemsOf(contexts));
      void Promise.all([queryKeys.profile, queryKeys.tasks].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
    onError: (_error, { ownerId }) => {
      if (useAuthStore.getState().user?.id !== ownerId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
  useEffect(() => setInvalid(null), [items]);

  const rename = (index: number, value: string) => setItems((list) => list.map((item, i) => (i === index ? { ...item, value } : item)));
  const remove = (index: number) => setItems((list) => list.filter((_, i) => i !== index));
  const move = (index: number, by: -1 | 1) =>
    setItems((list) => {
      const next = [...list];
      [next[index], next[index + by]] = [next[index + by], next[index]];
      return next;
    });
  const add = () => {
    if (!added.trim()) return;
    setItems((list) => [...list, { original: null, value: added.trim() }]);
    setAdded('');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validateGtdContexts(items.map((item) => item.value));
    if (!result.ok) {
      setInvalid(`${result.error}.`);
      return;
    }
    mutation.mutate({ contexts: result.contexts, renames: contextRenames(items), ownerId: useAuthStore.getState().user?.id });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-on-surface-variant">
        Where a next action can be done. Today groups next actions by these, in this order, and the AI picks one only from this list.
        Renaming a context renames it on your tasks too.
      </p>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center gap-2">
            <input
              className={`${inputClass} min-w-0 flex-1`}
              value={item.value}
              onChange={(e) => rename(index, e.target.value)}
              aria-label={`Context ${index + 1}`}
            />
            <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${item.value} up`} className={outlineButton}>
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => move(index, 1)}
              disabled={index === items.length - 1}
              aria-label={`Move ${item.value} down`}
              className={outlineButton}
            >
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => remove(index)}
              disabled={items.length === 1}
              aria-label={`Remove ${item.value}`}
              className={`${outlineButton} text-error`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input
          className={`${inputClass} min-w-0 flex-1`}
          value={added}
          onChange={(e) => setAdded(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="New context, e.g. Garage"
          aria-label="New context"
        />
        <button type="button" onClick={add} disabled={!added.trim() || items.length >= CONTEXTS_MAX} className={outlineButton}>
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          Save contexts
        </button>
        <button type="button" onClick={() => setItems(itemsOf(profile.gtd_contexts))} disabled={mutation.isPending} className={outlineButton}>
          Undo changes
        </button>
      </div>
      <SaveState mutation={mutation} invalid={invalid} notice={notice} />
    </form>
  );
}
